/* eslint-disable camelcase */

import { convertImageAndElementToCanvas } from 'app/CornerstoneTools/cornerstoneElementToBlob';
import { DataImage } from 'app/interfaces/DataImage';
import { DicomData, DicomTransferSyntax, DicomPixelDataType } from 'app/interfaces/Dicom';
import IDicomBuilder, {
  EncodedTransferSyntaxes,
  ImageEncoders,
} from 'app/interfaces/IDicomBuilder';
import { IDisplayableImageEncoder } from 'app/interfaces/IDisplayableImageEncoder';
import { DisplayableImageData, ImageOverlayData, Size, Viewport } from 'app/interfaces/Image';
import { Transformation, VOI } from 'app/interfaces/Image/Transformation';
import { IImageTransformer, IImageTransformerFactory } from 'app/interfaces/ImageTransformer';
import logger from 'app/utils/debug/logger';
import { getPixelSpacing } from 'app/utils/dicom/DicomData';
import { generateBitOverlayFromCanvas } from 'app/utils/imageManipulation/ImageTransformation';
import _ from 'lodash';

type ImageOverlayDataWithViewport = ImageOverlayData & { viewport: Viewport };

const computeBitsStored = (bitDepth: number) => {
  if (bitDepth > 12) return 16;
  if (bitDepth > 10) return 12;
  if (bitDepth > 8) return 10;
  return 8;
};

let imageIdCounter = 0;
const insertAnnotationsInImage = async (
  imageTransformer: IImageTransformer,
  displayableImageEncoders: IDisplayableImageEncoder,
  { annotations, metadata, viewport }: ImageOverlayDataWithViewport,
  originalImageSize: Size
) => {
  if (!annotations) return imageTransformer;
  const { width, height } = originalImageSize;
  const emptyImagePixelData = new Uint8Array(width * height);
  const [rowPixelSpacing, columnPixelSpacing] = metadata?.pixelSpacing ?? [];
  const emptyImage = {
    // eslint-disable-next-line no-plusplus
    imageId: `rawUInt16:${imageIdCounter++}`,
    minPixelValue: 0,
    maxPixelValue: 255,
    slope: 1,
    intercept: 0,
    windowCenter: 128,
    windowWidth: 256,
    getPixelData: () => emptyImagePixelData,
    rows: height,
    columns: width,
    height,
    width,
    color: false,
    columnPixelSpacing,
    rowPixelSpacing,
    invert: false,
    sizeInBytes: width * height,
  } as any as DisplayableImageData;

  const annotationsCanvas = await displayableImageEncoders.toCanvas(emptyImage, {
    annotations,
    viewport,
  });

  return imageTransformer.insertCanvasMask(annotationsCanvas);
};

async function generateAnnotationsOverlay(
  displayableImageEncoders: IDisplayableImageEncoder,
  { annotations, metadata, viewport }: ImageOverlayDataWithViewport,
  originalImageSize: Size
) {
  if (!annotations) return undefined;
  const { width, height } = originalImageSize;
  const emptyImagePixelData = new Uint8Array(width * height);
  const [rowPixelSpacing, columnPixelSpacing] = metadata?.pixelSpacing ?? [];
  const emptyImage = {
    // eslint-disable-next-line no-plusplus
    imageId: `rawUInt16:${imageIdCounter++}`,
    minPixelValue: 0,
    maxPixelValue: 255,
    slope: 1,
    intercept: 0,
    windowCenter: 128,
    windowWidth: 256,
    getPixelData: () => emptyImagePixelData,
    rows: height,
    columns: width,
    height,
    width,
    color: false,
    columnPixelSpacing,
    rowPixelSpacing,
    invert: false,
    sizeInBytes: width * height,
  } as any as DisplayableImageData;

  const annotationsCanvas = await displayableImageEncoders.toCanvas(emptyImage, {
    annotations,
    viewport,
  });
  return generateBitOverlayFromCanvas(annotationsCanvas);
}

export default class DicomBuilder implements IDicomBuilder {
  constructor(
    private imageTransformerFactory: IImageTransformerFactory,
    private displayableImageEncoders: IDisplayableImageEncoder,
    private imageEncoders?: ImageEncoders
  ) {}

  private async encodePixelData(
    img: IImageTransformer,
    transferSyntax?: EncodedTransferSyntaxes
  ): Promise<DicomPixelDataType | undefined> {
    if (!transferSyntax) {
      return undefined;
    }
    const encoder = this.imageEncoders[transferSyntax];
    if (!encoder) {
      logger.warn(`No encoder found for transferSyntax : ${transferSyntax}`);
      return undefined;
    }

    if (transferSyntax === DicomTransferSyntax.JPEGLSLossless) {
      const encodeOptions = {
        frameInfo: {
          width: img.info.width,
          height: img.info.height,
          bitsPerSample: img.info.bitDepth,
          componentCount: img.info.components,
        },
      };

      try {
        const pixelData = await encoder.encode(img.toBuffer(), encodeOptions);
        return [new Uint32Array(0), pixelData];
      } catch (e) {
        console.error(`Encode error for ${transferSyntax}`, e);
      }
    }

    return undefined;
  }

  private async injectPixelData(
    dicomData: DicomData,
    img: IImageTransformer,
    transformation: Transformation = {},
    voi: VOI,
    transferSyntax?: EncodedTransferSyntaxes,
    overlay?: ImageOverlayData
  ): Promise<DicomData> {
    const start = performance.now();
    let { windowWidth, windowCenter } = voi;
    const { invert, rotation, hflip, vflip, crop } = transformation;
    const { width: initialWidth, height: initialHeight } = img.info;
    // We do not apply VOI and invert since DicomSpecification allow specifying them.
    img = img.transform({ rotation, hflip, vflip, crop });
    const dicomOverlay: DicomData = {};
    if (overlay) {
      const pixelSpacing = getPixelSpacing(dicomData);
      const overlayWithSpacing = _.merge({}, overlay, {
        metadata: { pixelSpacing },
        viewport: { hflip, vflip, rotation },
      });

      const markerAnnotationsWithoutCrop = _.omitBy(
        _.pick(overlay.annotations, ['LeftMarker', 'RightMarker']),
        _.isEmpty
      );
      const otherAnnotationsWithoutCrop = _.omitBy(
        _.omit(overlay.annotations, ['LeftMarker', 'RightMarker', 'Crop']),
        _.isEmpty
      );

      let imageTransformer = img;
      // We insert markers directly inside pixel data to make them visible even in the bad DICOM viewer.
      if (!_.isEmpty(markerAnnotationsWithoutCrop)) {
        imageTransformer = await insertAnnotationsInImage(
          img,
          this.displayableImageEncoders,
          {
            ...overlayWithSpacing,
            annotations: { ...overlay.annotations },
          },
          {
            width: initialWidth,
            height: initialHeight,
          }
        );
      }

      // User can now choose to insert other annotations from a checkbox. This makes it more likely for them to understand that these annotations are saved in the DICOM. We no longer need the overlaydata to extract them from pixel data.

      // Less important annotations are inserted as overlay.
      // if (!_.isEmpty(otherAnnotationsWithoutCrop)) {
      //   const bitOverlay = await generateAnnotationsOverlay(
      //     this.displayableImageEncoders,
      //     {
      //       ...overlayWithSpacing,
      //       annotations: { ...otherAnnotationsWithoutCrop, Crop: overlay.annotations.Crop },
      //     },
      //     {
      //       width: initialWidth,
      //       height: initialHeight,
      //     }
      //   );
      //   const overlayGroup = '6000'; // First overlay plane
      //   dicomOverlay[`${overlayGroup}0010`] = { VR: 'US', data: img.info.height }; // Overlay Rows
      //   dicomOverlay[`${overlayGroup}0011`] = { VR: 'US', data: img.info.width }; // Overlay Columns
      //   dicomOverlay[`${overlayGroup}3000`] = { VR: 'OB', data: bitOverlay }; // Overlay Data
      //   dicomOverlay[`${overlayGroup}0050`] = { VR: 'SS', data: [1, 1] }; // Overlay Origin (X, Y)
      //   dicomOverlay[`${overlayGroup}0100`] = { VR: 'US', data: 1 }; // Overlay Bits Allocated
      //   dicomOverlay[`${overlayGroup}0102`] = { VR: 'US', data: 0 }; // Overlay Bit Position
      //   dicomOverlay[`${overlayGroup}0040`] = { VR: 'CS', data: 'G' }; // Overlay Type (Graphics)
      // }
    }

    const { bitDepth, width, height } = img.info;

    let dicomTransferSyntax = transferSyntax;
    let pixelDataVR = 'OB';
    let pixelData = await this.encodePixelData(img, transferSyntax);

    // In case encoding failed or should not return anything, we default to native format.
    if (!pixelData) {
      dicomTransferSyntax = DicomTransferSyntax.ImplicitVRLittleEndian;
      pixelData = img.toBuffer();

      if (bitDepth > 8) pixelDataVR = 'OW';
    }

    const minValue = img.getMin();
    const maxValue = img.getMax();

    if (!(windowWidth && windowCenter)) {
      windowWidth = maxValue - minValue;
      windowCenter = Math.round(windowWidth / 2 + minValue);
    }

    // See DICOM specification part03 sect_C.8.7.1.1.6
    const bitsAllocated = bitDepth > 8 ? 16 : 8;
    const bitsStored = computeBitsStored(bitDepth);
    const highBit = bitsStored - 1;

    const dicomPixelData = {
      TransferSyntaxUID: dicomTransferSyntax,
      SamplesPerPixel: 1,
      PhotometricInterpretation: invert ? 'MONOCHROME1' : 'MONOCHROME2',
      Rows: height,
      Columns: width,
      BitsAllocated: bitsAllocated,
      BitsStored: bitsStored,
      HighBit: highBit,
      PixelRepresentation: 0,
      SmallestImagePixelValue: { data: minValue, VR: 'US' },
      LargestImagePixelValue: { data: maxValue, VR: 'US' },
      BurnedInAnnotation: 'NO',
      PixelIntensityRelationship: 'LOG',
      PixelIntensityRelationshipSign: 1,
      WindowCenter: windowCenter.toFixed(0),
      WindowWidth: windowWidth.toFixed(0),
      RescaleIntercept: '0',
      RescaleSlope: '1',
      RescaleType: 'US',
      LossyImageCompression: '00',
      PixelData: { data: pixelData, VR: pixelDataVR },
      ...dicomOverlay,
    };

    const end = performance.now();
    logger.log(`DicomBuilder.injectPixelData took ${end - start}ms`);
    return { ...dicomData, ...dicomPixelData };
  }

  injectPixelDataFromDisplayableImage = async (
    dicomData: DicomData,
    image: DisplayableImageData,
    transformation: Transformation,
    voi: VOI,
    transferSyntax?: EncodedTransferSyntaxes,
    overlay?: ImageOverlayData
  ): Promise<DicomData> => {
    const img = this.imageTransformerFactory.fromDisplayableImage(image);

    return this.injectPixelData(dicomData, img, transformation, voi, transferSyntax, overlay);
  };

  injectProcessedImageData = async (
    dicomData: DicomData,
    processedImage: DataImage,
    transformation: Transformation,
    voi: VOI,
    transferSyntax?: EncodedTransferSyntaxes,
    overlay?: ImageOverlayData
  ): Promise<DicomData> => {
    if (!processedImage) return dicomData;

    const img = this.imageTransformerFactory.fromDataImage(processedImage);

    return this.injectPixelData(dicomData, img, transformation, voi, transferSyntax, overlay);
  };
}
